// The active language (D70): saved per browser, else the first of the browser's languages we
// speak. English is bundled; French and Spanish load as their own chunk when chosen. Components
// read the messages with useT(), which re-renders them on a change; plain modules read
// messages() when they are called.
import { useSyncExternalStore } from "react";

import { isLocale, type Locale, negotiate } from "./locales";
import { en, type Messages } from "./messages/en";

const KEY = "ocs.locale";

const loaders: Record<Locale, () => Promise<Messages>> = {
  en: () => Promise.resolve(en),
  fr: () => import("./messages/fr").then((m) => m.fr),
  es: () => import("./messages/es").then((m) => m.es),
};

let state: { locale: Locale; messages: Messages } = { locale: "en", messages: en };
const listeners = new Set<() => void>();

function saved(): Locale | undefined {
  try {
    const value = localStorage.getItem(KEY);
    return isLocale(value) ? value : undefined;
  } catch {
    // Storage can be blocked (private mode); fall back to the browser's languages.
    return undefined;
  }
}

/** The language to start in: the saved choice, else the browser's. */
export function preferredLocale(): Locale {
  return saved() ?? negotiate(navigator.languages ?? [navigator.language]);
}

let requested: Locale = "en";

async function apply(locale: Locale): Promise<void> {
  requested = locale;
  const messages = await loaders[locale]();
  // A later choice made while this one loaded wins.
  if (requested !== locale) return;
  state = { locale, messages };
  document.documentElement.lang = locale;
  listeners.forEach((l) => l());
}

/** Loads the preferred language before the first render. */
export function initLocale(): Promise<void> {
  return apply(preferredLocale());
}

/** Switches language and remembers the choice in this browser. */
export async function setLocale(locale: Locale): Promise<void> {
  await apply(locale);
  try {
    localStorage.setItem(KEY, locale);
  } catch {
    // Not persisting the choice is fine.
  }
}

/** The active language's messages, for code outside components. */
export function messages(): Messages {
  return state.messages;
}

export function currentLocale(): Locale {
  return state.locale;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

const snapshot = () => state;

/** The active language's messages; the component re-renders when the language changes. */
export function useT(): Messages {
  return useSyncExternalStore(subscribe, snapshot).messages;
}

export function useLocale(): Locale {
  return useSyncExternalStore(subscribe, snapshot).locale;
}
